import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import { InsertHistoryDocument } from 'generated/graphql';
import { SourceTypeEnum } from 'interfaces/enums';
import { v4 as uuidv4 } from 'uuid';
import { logout, renewSession } from './utilities/auth';

const sourceTypes = Object.keys(SourceTypeEnum)
  .filter((key) => !isNaN(Number(key)))
  .map((key) => {
    return { value: Number(key), title: SourceTypeEnum[key].toString() };
  });

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_ENV,
});

const isAuthGraphQLError = (message: string) => {
  return /jwt|authorization header|could not verify/i.test(message);
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const hasRetried = operation.getContext().authRetried;
  const hasAuthGraphQLError = (graphQLErrors || []).some((error) => isAuthGraphQLError(error.message));
  const hasUnauthorizedNetworkError = 'statusCode' in (networkError || {}) && (networkError as any).statusCode === 401;

  if ((hasAuthGraphQLError || hasUnauthorizedNetworkError) && !hasRetried) {
    operation.setContext({ authRetried: true });

    return new Observable((observer) => {
      let subscription: { unsubscribe: () => void } | null = null;

      void renewSession()
        .then((renewedToken) => {
          if (!renewedToken) {
            logout(true);
            observer.error(networkError || new Error('Unable to renew session'));
            return;
          }

          const context = operation.getContext();

          operation.setContext({
            ...context,
            headers: {
              ...(context.headers || {}),
              authorization: `Bearer ${renewedToken}`,
            },
          });

          subscription = forward(operation).subscribe({
            next: (result) => observer.next(result),
            error: (error) => observer.error(error),
            complete: () => observer.complete(),
          });
        })
        .catch((error) => {
          observer.error(error);
        });

      return () => {
        if (subscription) {
          subscription.unsubscribe();
        }
      };
    });
  }
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem('token');
  // return the headers to the context so httpLink can read them
  return {
    headers: token
      ? {
          ...headers,
          authorization: `Bearer ${token}`,
        }
      : {
          ...headers,
        },
  };
});

const historyLink = new ApolloLink((operation, forward) => {
  const opName = operation.operationName.toLowerCase();
  const isEdit = opName.indexOf('insert') > -1 || opName.indexOf('delete') > -1 || opName.indexOf('update') > -1;

  return forward(operation).map((response) => {
    const ctx = operation.getContext();

    let isValid =
      isEdit && [200, 201, 204].indexOf(ctx.response?.status) > -1 && operation.operationName !== 'InsertHistory';

    if (isValid) {
      const contactId = localStorage.getItem('contactId');
      const displayName = localStorage.getItem('displayName');
      const emailAddress = localStorage.getItem('emailAddress');
      const payload = JSON.stringify(operation.variables);
      let source = operation.operationName;

      if (source.indexOf('Bulk') === -1) {
        source = source
          .replace('Detail', '')
          .replace('Insert', '')
          .replace('Update', '')
          .replace('Delete', '')
          .replace('Associations', '');

        if (source.endsWith('s') && source !== 'AccountAddress') {
          source = source.substring(0, source.length - 1);
        }
      }

      let sourceType = sourceTypes.find((x) => source === x.title);
      const variables = {
        object: {
          ContactId: contactId,
          Description: `${operation.operationName} by ${displayName} (${emailAddress}). Payload: ${payload}`,
          OccuredOn: new Date(),
          SourceId: process.env.REACT_APP_UNIQUE_ID,
          SourceType: sourceType?.value || 0,
          HistoryId: uuidv4(),
        },
      };
      apolloClient.mutate({ mutation: InsertHistoryDocument, variables: variables }).catch(() => {
        console.error('History failed');
      });
    }
    return response;
  });
});

export const apolloClient = new ApolloClient({
  link: errorLink.concat(historyLink).concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
});

export default apolloClient;
